import { useState, useRef } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Upload, Link, X, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check if URL is a Google Drive link
const isGoogleDriveUrl = (url) => {
  if (!url) return false;
  return url.includes('drive.google.com') || url.includes('drive.usercontent.google.com');
};

export default function GalleryUpload({ 
  images = [], 
  onChange, 
  token 
}) {
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const fileInputRef = useRef(null);

  // Handle Google Drive URL - download and store locally
  const handleGoogleDriveUrl = async (url) => {
    if (!isGoogleDriveUrl(url)) return url;
    
    setDownloading(true);
    try {
      const response = await axios.post(`${API}/uploads/from-url`, { url }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.data.success) {
        toast.success('Image saved from Google Drive!');
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        return `${baseUrl}${response.data.url}`;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to download from Google Drive';
      toast.error(detail);
    } finally {
      setDownloading(false);
    }
    return null;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File is too large. Maximum size is 10MB');
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/uploads`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      onChange([...images, response.data.url]);
      toast.success('Image uploaded successfully');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAddUrl = async () => {
    if (urlValue.trim()) {
      let finalUrl = urlValue.trim();
      
      // If it's a Google Drive URL, download and store locally
      if (isGoogleDriveUrl(finalUrl)) {
        const localUrl = await handleGoogleDriveUrl(finalUrl);
        if (localUrl) {
          finalUrl = localUrl;
        } else {
          return; // Download failed, error already shown
        }
      }
      
      onChange([...images, finalUrl]);
      setUrlValue('');
      setShowUrlInput(false);
    }
  };

  const removeImage = (index) => {
    onChange(images.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {/* Gallery Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((img, idx) => (
            <div key={idx} className="relative group">
              <div className="w-full h-20 rounded overflow-hidden border">
                <img 
                  src={img} 
                  alt={`Gallery ${idx + 1}`} 
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Image Options */}
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileChange}
          className="hidden"
        />
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <Upload className="h-3 w-3 mr-1" />
          )}
          Upload
        </Button>
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowUrlInput(!showUrlInput)}
        >
          <Link className="h-3 w-3 mr-1" />
          Add URL
        </Button>
      </div>

      {/* URL Input */}
      {showUrlInput && (
        <div className="flex gap-2">
          <Input
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="flex-1"
            onKeyDown={(e) => e.key === 'Enter' && handleAddUrl()}
          />
          <Button
            type="button"
            size="sm"
            onClick={handleAddUrl}
          >
            <Plus className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowUrlInput(false);
              setUrlValue('');
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {images.length === 0 && !showUrlInput && (
        <p className="text-xs text-muted-foreground italic">No gallery images</p>
      )}
    </div>
  );
}
